import React, {
    SyntheticEvent,
    useRef,
    useEffect,
    useState,
    memo,
    SetStateAction,
    Dispatch,
} from 'react';
import { Button } from 'react-bootstrap';
import { Formik } from 'formik';
import * as yup from 'yup';
import { MatchAdmin } from '../../../models/interfaces/Match';
import { Stat } from '../../../models/interfaces/Stat';
import {
    MatchStatFormValues,
    HandleSubmitNewStatProps,
    HandleSubmitSelectedStatProps,
    DisplayTextObj,
    ToastDetails,
} from '../../interfaces/MatchUI';
import ModalStatFormDisplay from './ModalStatFormDisplay';
import { PerformerTypes, TableWorker } from '../../../models/interfaces/Stat';
import { RosterEntry } from '../../../models/interfaces/RosterEntry';

const displayTextObjInitialValues: DisplayTextObj = {
    titleText: '',
    buttonText: 'Save',
};

const displayTextObjEditValues: DisplayTextObj = {
    titleText: 'Edit Game Stat',
    buttonText: 'Update',
};
interface Props {
    matchAdmin: MatchAdmin;
    editValues?: MatchStatFormValues;
    editingStat?: Stat;

    handleSubmitNewStat?: ({
        matchSegment,
        timestamp,
        performer,
        selectedStatDescription,
        resetForm,
    }: HandleSubmitNewStatProps) => void;
    handleSubmitEditingStat?: ({
        stat,
        matchSegment,
        timestamp,
        performer,
        selectedStatDescription,
        resetForm,
    }: HandleSubmitSelectedStatProps) => void;
    handleClearForm: ({
        handleReset,
    }: {
        handleReset: (e?: SyntheticEvent<any, Event> | undefined) => void;
    }) => void;
    lastStatPeriod?: number | undefined;
    setToastDetails: Dispatch<SetStateAction<ToastDetails>>;
    tabSelected: boolean;
    lightCapPlayerList: RosterEntry[] | undefined;
    darkCapPlayerList: RosterEntry[] | undefined;
}

const INITIALFORMVALUES: MatchStatFormValues = {
    selectedTeam: '',
    selectedPlayer: '',
    selectedPeriod: '',
    selectedMinutes: '',
    selectedSeconds: '',
    selectedStat: '',
    selectedRemark: '',
};

const MatchStatFormFrame = memo(
    ({
        matchAdmin,
        editValues,
        editingStat,
        handleSubmitNewStat,
        handleSubmitEditingStat,
        handleClearForm,
        lastStatPeriod,
        setToastDetails,
        tabSelected,
        lightCapPlayerList,
        darkCapPlayerList,
    }: Props) => {
        const { match } = matchAdmin;
        const totalSeconds = match.parameters.regularTimeDescription.durationInSeconds;

        const [initialValues, setInitialValues] = useState<MatchStatFormValues>(INITIALFORMVALUES);

        const [displayTextObj, setDisplayTextObj] = useState<DisplayTextObj>(
            displayTextObjInitialValues
        );
        const [showShortCutKeys, setShowShortCutKeys] = useState<boolean>(false);

        useEffect(() => {
            if (!tabSelected) {
                setShowShortCutKeys(false);
            }
        }, [tabSelected]);

        const formEl = useRef<HTMLFormElement>(null);

        const schema = yup.object({
            selectedMinutes: yup
                .number()
                .typeError('Must be a number')
                .required('Minutes Required')
                .integer()
                .min(0)
                .max(totalSeconds / 60, `Maximum of ${totalSeconds / 60} minutes`),
            selectedSeconds: yup
                .number()
                .typeError('Must be a number')
                .required('Seconds Required')
                .integer()
                .min(0)
                .max(60, 'Maximum of 60 seconds'),
            selectedTeam: yup.object().required('Please select a team'),
            selectedPlayer: yup.string().required('Please select a player'),
            selectedStat: yup.object().required('Please select an stat type'),
            selectedRemark: yup.string(),
        });

        useEffect(() => {
            //set form values if in edit mode
            if (editValues && editingStat) {
                setInitialValues(() => ({
                    selectedStat: editValues.selectedStat,
                    selectedTeam:
                        editValues.selectedStat !== '' && editValues.selectedStat.requiresRemark
                            ? {}
                            : !editValues.selectedTeam
                            ? ''
                            : !('id' in editValues.selectedTeam)
                            ? {}
                            : editValues.selectedTeam.id === match.lightCapTeam.id
                            ? match.lightCapTeam
                            : match.darkCapTeam,
                    // checks if player stat was selected & maps to that. if a team stat advise user this is not required
                    selectedPlayer:
                        editValues.selectedPlayer ||
                        (editValues.selectedStat !== '' &&
                            !editValues.selectedStat.typeDescription.supportedPerfomers.has(
                                PerformerTypes.player
                            ) &&
                            'notRequired') ||
                        '',
                    selectedPeriod:
                        editValues.selectedPeriod ||
                        match.parameters.regularTimeDescription.matchSegments[0].databaseValue,
                    selectedMinutes: editValues.selectedMinutes,
                    selectedSeconds: editValues.selectedSeconds,
                    selectedRemark: editValues.selectedRemark,
                }));
            } else if (!editingStat) {
                setInitialValues(() => {
                    if (lastStatPeriod)
                        return { ...INITIALFORMVALUES, selectedPeriod: lastStatPeriod };
                    return { ...INITIALFORMVALUES };
                });
            }
        }, [
            match.darkCapTeam,
            match.lightCapTeam,
            match.parameters.regularTimeDescription.matchSegments,
            editValues,
            editingStat,
            lastStatPeriod,
        ]);

        useEffect(() => {
            if (!editingStat) {
                setDisplayTextObj(displayTextObjInitialValues);
            } else {
                setDisplayTextObj(displayTextObjEditValues);
            }
        }, [setDisplayTextObj, editingStat]);

        return (
            <Formik
                validationSchema={schema}
                initialValues={initialValues}
                enableReinitialize={true}
                onSubmit={(
                    {
                        selectedTeam,
                        selectedPlayer,
                        selectedPeriod,
                        selectedMinutes,
                        selectedSeconds,
                        selectedStat,
                        selectedRemark,
                    },
                    actions
                ) => {
                    // if invalid string will produce NaN & be caught below
                    selectedPeriod = Number(selectedPeriod);
                    selectedMinutes = Number(selectedMinutes);
                    selectedSeconds = Number(selectedSeconds);

                    if (
                        !selectedTeam ||
                        !selectedPlayer ||
                        !(selectedPeriod >= 0) ||
                        !(selectedMinutes >= 0) ||
                        !(selectedSeconds >= 0) ||
                        !selectedStat
                    ) {
                        //this shouldn't occur due to validation but one final check & output to console
                        console.error('an error occurred & not all stats were selected');
                        console.error({
                            selectedTeam,
                            selectedPlayer,
                            selectedPeriod,
                            selectedMinutes,
                            selectedSeconds,
                            selectedStat,
                        });
                        // user feedback - this is a programmer error if it gets here
                        setToastDetails((details) => ({
                            ...details,
                            showToast: true,
                            toastText: 'An error occurred, saving the stat',
                            status: 'failed',
                        }));
                        actions.setSubmitting(false);
                        return undefined;
                    }
                    const matchSegment = match.parameters.regularTimeDescription.matchSegments
                        .concat(match.parameters.overtimeDescription.matchSegments)
                        .find((segment) => segment.databaseValue === Number(selectedPeriod));
                    const timestamp = selectedMinutes * 60 + selectedSeconds;

                    let performer;
                    if (
                        selectedStat.typeDescription.supportedPerfomers.has(PerformerTypes.player)
                    ) {
                        if (lightCapPlayerList && darkCapPlayerList) {
                            performer =
                                selectedTeam === match.lightCapTeam
                                    ? lightCapPlayerList.find(
                                          (player) => player.id === selectedPlayer
                                      )
                                    : darkCapPlayerList.find(
                                          (player) => player.id === selectedPlayer
                                      );
                            if (
                                // this is to catch if it's a stat that can be either team or player (e.g. red/yellow card) and we found out the selectedPlayer does not match a player we have been provided a the team option and need to update the performer as such
                                selectedStat.typeDescription.supportedPerfomers.has(
                                    PerformerTypes.team
                                ) &&
                                !performer
                            ) {
                                performer = selectedTeam;
                            }
                            //should only get into !performer if the user selected a non player for a selectedStat which requires a player to be included
                            else if (!performer) {
                                // sets the error under the select player.
                                actions.setFieldError(
                                    'selectedPlayer',
                                    `Please reselect the player & try again`
                                );
                                // gives user feedback if they tried to selected "team" for a player stat
                                setToastDetails((details) => ({
                                    ...details,
                                    showToast: true,
                                    toastText: `An error occurred saving the player.
                                        Please reselect the player & try again`,
                                    status: 'failed',
                                }));
                                actions.setSubmitting(false);

                                return undefined;
                            }
                        } else {
                            console.error('an error occurred loading team lists');
                            setToastDetails((details) => ({
                                ...details,
                                showToast: true,
                                toastText: 'An error occurred, saving the stat',
                                status: 'failed',
                            }));
                            actions.setSubmitting(false);
                        }
                    } else if (
                        selectedStat.typeDescription.supportedPerfomers.has(PerformerTypes.team)
                    ) {
                        performer = selectedTeam;
                    } else if (
                        selectedStat.typeDescription.supportedPerfomers.has(
                            PerformerTypes.tableWorker
                        )
                    ) {
                        performer = matchAdmin as TableWorker;
                    } else {
                        //we should not get to this stage but this should catch future performer types which have not been mapped
                        console.error('could not find a valid performer');
                        setToastDetails((details) => ({
                            ...details,
                            showToast: true,
                            toastText: 'Failed to find performer while saving the stat',
                            status: 'failed',
                        }));
                        actions.setSubmitting(false);
                    }

                    const selectedStatDescription = selectedStat.typeDescription;
                    const remarks = selectedRemark.length > 0 ? selectedRemark : undefined;

                    if (selectedStat.requiresRemark && !remarks) {
                        actions.setFieldError(
                            'selectedRemark',
                            'Selected Stat Requires for a remark to be provided'
                        );
                        actions.setSubmitting(false);
                        return undefined;
                    }
                    if (
                        !matchSegment ||
                        !(timestamp >= 0) ||
                        !performer ||
                        !selectedStatDescription
                    ) {
                        console.error(`an error occurred assigning values to backend variables`);
                        console.error({
                            matchSegment,
                            timestamp,
                            performer,
                            selectedStatDescription,
                        });

                        // users get a more general feedback
                        setToastDetails((details) => ({
                            ...details,
                            showToast: true,
                            toastText: 'An error occurred while saving the stat',
                            status: 'failed',
                        }));
                        actions.setSubmitting(false);

                        return undefined;
                    }
                    if (editingStat && handleSubmitEditingStat) {
                        if (editingStat) {
                            handleSubmitEditingStat({
                                stat: editingStat,
                                matchSegment,
                                timestamp,
                                performer,
                                selectedStatDescription,
                                remarks,
                                resetForm: actions.resetForm,
                                setSubmitting: actions.setSubmitting,
                            });
                        }
                    } else if (handleSubmitNewStat) {
                        handleSubmitNewStat({
                            matchSegment,
                            timestamp,
                            performer,
                            selectedStatDescription,
                            remarks,
                            resetForm: actions.resetForm,
                            setSubmitting: actions.setSubmitting,
                        });
                    }
                    if (showShortCutKeys) {
                        setShowShortCutKeys(false);
                    }
                }}
            >
                {({
                    handleSubmit,
                    handleChange,
                    values,
                    touched,
                    errors,
                    setFieldValue,
                    handleReset,
                    isSubmitting,
                }) => {
                    const formikProps = {
                        handleSubmit,
                        handleChange,
                        values,
                        touched,
                        errors,
                        setFieldValue,
                        handleReset,
                        isSubmitting,
                    };

                    return (
                        <ModalStatFormDisplay
                            matchAdmin={matchAdmin}
                            displayTextObj={displayTextObj}
                            formikProps={formikProps}
                            formEl={formEl}
                            totalSeconds={totalSeconds}
                            handleClearForm={handleClearForm}
                            teamLists={{ darkCapPlayerList, lightCapPlayerList }}
                            showShortCutKeys={showShortCutKeys}
                            setShowShortCutKeys={setShowShortCutKeys}
                            isSelectedTab={tabSelected}
                        />
                    );
                }}
            </Formik>
        );
    }
);

export default MatchStatFormFrame;
